import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { teamLicensesActions, teamMembersActions } from '../redux/actions';
import TeamForm from './TeamForm';
import LabeledSelect from './LabeledSelect';
import { CardLoader } from './Loader';
import useError from '../utils/HookErrorUtil';
import { TEAM_TYPE_ORGANIZATION } from '../constants';

/**
 * Filter teams to only include organization teams where user is a member (not owner)
 */
const filterOrganizationTeams = (teams, userId) => {
  const teamsArray = Object.values(teams);
  return teamsArray.filter((tm) => (
    tm.type === TEAM_TYPE_ORGANIZATION && tm.owner.id !== userId
  ));
};

/**
 * Component to display organization teams that a practitioner has joined.
 * Similar to TeamForms but only shows organization teams with a select dropdown.
 */
function JoinedOrganizationTeamForms(props) {
  const { handleCatched } = useError();
  const { t } = useTranslation();
  const { id: paramId } = useParams();

  const [loading, setLoading] = useState(true);
  const [currentTeamId, setCurrentTeamId] = useState(undefined);

  const userId = useSelector((state) => state.auth.authUser.id);
  const tmLicenses = useSelector((state) => state.teamLicenses);
  const tms = useSelector((state) => state.teams);

  const teamLicenses = useMemo(() => Object.values(tmLicenses), [tmLicenses]);
  const teams = useMemo(
    () => filterOrganizationTeams(tms, userId),
    [tms, userId],
  );

  const dispatch = useDispatch();

  const fetchTeamLicenses = async () => dispatch(teamLicensesActions.list(
    {}, { pagination: 'no', useCache: true },
  ));
  const fetchTeamMembers = async (teamId) => dispatch(teamMembersActions.list(
    { team: teamId }, { pagination: 'no' },
  ));

  const getTeamMembers = async (team) => {
    setLoading(true);
    try {
      await fetchTeamMembers(team.id);
    } catch (error) {
      handleCatched(props, error, false);
    } finally {
      setLoading(false);
    }
  };

  const selectTeam = async (team) => {
    if (team) await getTeamMembers(team);
    setCurrentTeamId(team ? team.id : undefined);
  };

  const selectTeamFromId = async (id) => {
    const team = teams.find((tm) => tm.id === Number(id));
    await selectTeam(team);
    return team;
  };

  const selectFirstTeam = async () => {
    const team = teams.length ? teams[0] : undefined;
    await selectTeam(team);
    return team;
  };

  const fetchData = async () => {
    try {
      await fetchTeamLicenses();

      // Team selection
      if (paramId) await selectTeamFromId(paramId);
      else await selectFirstTeam();
    } catch (error) {
      handleCatched(props, error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [paramId]);

  const renderSelectTeam = () => (
    <div className="row d-flex justify-content-between align-items-start mt-4 mb-4 mx-4 mx-md-0">
      {
        Boolean(teams.length) && (
          <div className="col-12 col-sm-auto px-0">
            <div className="row align-items-center mx-0">
              <LabeledSelect
                name="team"
                label={(<span className="text-bigger">{t('user:team.select')}</span>)}
                className="col-12 col-lg-auto mb-0"
                rowClassName="row align-items-center"
                selectClassName="custom-select team-select"
                colLabelClassName="col-12 col-md-auto pl-0 mt-1"
                colSelectClassName="col-12 col-md-auto pl-0 mb-3 mb-sm-0"
                validatorClassName="w-100"
                hideOptionalLabel
                value={currentTeamId}
                onChange={(e) => selectTeamFromId(e.target.value)}
              >
                {
                  teams.map((team) => (
                    <option
                      key={team.id}
                      value={team.id}
                    >
                      {team.name}
                    </option>
                  ))
                }
              </LabeledSelect>
            </div>
          </div>
        )
      }
    </div>
  );

  // No organization teams joined
  if (!loading && teams.length === 0) {
    return (
      <div className="text-center practitioner-no-team">
        <h6>
          {t('user:team.no-joined-org-team')}
        </h6>
      </div>
    );
  }

  return (
    <div>
      {loading && <CardLoader />}
      {teams.length > 1 && renderSelectTeam()}
      {currentTeamId && (
        <TeamForm
          key={`team-form-${currentTeamId}`}
          team={teams.find((tm) => tm.id === currentTeamId)}
          teamLicenses={teamLicenses}
          {...props}
        />
      )}
    </div>
  );
}

export default JoinedOrganizationTeamForms;
